import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "./cart";
import { useLang, LangToggle } from "./i18n";

const LINKS = [
  ["nav.new", "#nouveautes"],
  ["nav.collections", "#collections"],
  ["nav.lookbook", "#lookbook"],
];

export const go = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Nav = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const { t } = useLang();
  const loc = useLocation();
  const navigate = useNavigate();
  const light = loc.pathname !== "/";
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 80);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const headCls = light
    ? `text-black transition-[background-color,backdrop-filter] duration-300 ${scrolled ? "bg-[#F5F5F7]/85 backdrop-blur-xl border-b border-black/10" : ""}`
    : "mix-blend-difference text-white";
  const nav = (h) => {
    setMenu(false);
    if (loc.pathname !== "/") navigate("/" + (h === "#top" ? "" : h));
    else go(h);
  };

  return (
    <>
    <header data-testid="site-header" className={`fixed top-[38px] inset-x-0 z-40 ${headCls}`}>
      <div className="flex items-center justify-between px-5 md:px-10 h-16">
        <button data-testid="nav-logo" onClick={() => nav("#top")} className="flex items-center gap-3 group">
          <img src={light ? "/img/logo.png" : "/img/logo-white.png"} alt="Off Grid" className="w-10 h-10 transition-transform duration-500 group-hover:rotate-[360deg]" />
          <span className="f-goth text-2xl leading-none">Off Grid</span>
        </button>
        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map(([l, h]) => (
            <button key={h} data-testid={`nav-link-${h.slice(1)}`} onClick={() => nav(h)} className="og-link f-mono text-xs uppercase tracking-[0.2em]">
              {t(l)}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-5">
          <LangToggle className="hidden md:flex" />
          <button data-testid="nav-cart-button" onClick={() => setOpen(true)} className="relative flex items-center gap-2 f-mono text-xs uppercase tracking-[0.2em]">
            <ShoppingBag className="w-5 h-5" />
            <span className="hidden sm:inline">{t("nav.cart")}</span>
            <span data-testid="nav-cart-count" className="tabular-nums">[{count}]</span>
          </button>
          <button data-testid="nav-menu-toggle" className="md:hidden" onClick={() => setMenu((m) => !m)} aria-label="Menu">
            {menu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </header>
      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
            className="md:hidden fixed inset-x-0 top-[102px] bottom-0 z-40 bg-white text-black px-6 py-10 flex flex-col gap-4"
          >
            {LINKS.map(([l, h], i) => (
              <button key={h} data-testid={`mobile-link-${h.slice(1)}`} onClick={() => nav(h)} className="text-left f-disp text-6xl uppercase">
                <span className="f-mono text-xs align-top mr-3 text-[#FF2A2A]">0{i + 1}</span>{t(l)}
              </button>
            ))}
            <LangToggle className="mt-6 text-base" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
